import React from "react";
import { Box, Grid, Alert } from "@mui/material";
import FormCheckboxGroup from "@/components/FormCheckboxGroup";
import FormNumberInput from "@/components/FormNumberInput";
import AsyncSelectWithSearch from "@/components/AsyncSelectWithSearch";
import FormRadioList from "@/components/FormRadioList";
import FormCheckbox from "@/components/FormCheckbox";
import { InfoIcon } from "@/components/InfoIcon";
import { SectionTitle, SectionDivider } from "./SizingToolFormLayout";
import { SizingFormData } from "@/pages/ProjectDetailsPage/components/ProjectInfoCard/types";
import { FormValidationValue } from "@/types";

interface SystemSpecificationsSectionProps {
  formInstance: any;
  sizingForm: SizingFormData;
  setSizingForm: (form: SizingFormData) => void;
  disabled?: boolean;
  checkFieldsDisabled?: boolean;
  getBatteryOems: (searchValue: string) => Promise<any>;
  getBatteryList: (oem: string, searchValue: string) => Promise<any>;
  onBatteryProductChange?: (productName: string) => void;
  showNewFields?: boolean;
  showSolarSystemCheckboxes?: boolean;
}

export const SystemSpecificationsSection: React.FC<
  SystemSpecificationsSectionProps
> = ({
  formInstance,
  sizingForm,
  setSizingForm,
  disabled = false,
  checkFieldsDisabled,
  getBatteryOems,
  getBatteryList,
  onBatteryProductChange,
  showNewFields = false,
  showSolarSystemCheckboxes = false,
}) => {
  const batteryManufacturerId = formInstance.watch("batteryManufacturerId");
  const solarSystemType = formInstance.watch("solarSystemType");
  const existingSolarLessThan12Months = formInstance.watch(
    "existingSolarLessThan12Months"
  );
  const hasExistingBattery = formInstance.watch("hasExistingBattery");
  const checkFieldsAreDisabled = checkFieldsDisabled ?? disabled;

  const hasExistingSolar =
    Array.isArray(solarSystemType) && solarSystemType.includes("existing");
  const hasNewSolar =
    Array.isArray(solarSystemType) && solarSystemType.includes("new");

  const createSolarFieldValidator = (systemType: "existing" | "new") => {
    const isSelected =
      systemType === "existing" ? hasExistingSolar : hasNewSolar;
    const systemName = systemType === "existing" ? "Existing" : "New";

    return (value: number) => {
      if (isSelected && (value === undefined || value === null || value <= 0)) {
        return `Required when ${systemName} Solar System is selected`;
      }
      return true;
    };
  };

  const getBatteryListWithId = React.useCallback(
    async (searchValue: string) => {
      try {
        if (!batteryManufacturerId) {
          return { data: { batteryModels: [] } };
        }
        return await getBatteryList(String(batteryManufacturerId), searchValue);
      } catch (error) {
        return { data: { batteryModels: [] } };
      }
    },
    [batteryManufacturerId, getBatteryList]
  );

  return (
    <>
      <SectionTitle>System Specifications</SectionTitle>
      <SectionDivider />

      {showSolarSystemCheckboxes && (
        <Box
          sx={{
            mb: 0,
            "& .MuiFormLabel-root": { marginBottom: 0 },
            "& .MuiFormControl-root": { marginBottom: 0 },
            "& .MuiFormLabel-root > div": { paddingBottom: 0 },
          }}
        >
          <FormCheckboxGroup
            name="solarSystemType"
            control={formInstance.control}
            label="Solar Systems"
            multiple={true}
            disabled={checkFieldsAreDisabled}
            items={[
              { value: "existing", label: "Existing Solar System" },
              { value: "new", label: "New Solar System" },
            ]}
            controlProps={{ rules: { required: "Required" } }}
          />
        </Box>
      )}

      {hasExistingSolar && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <FormNumberInput
              name="existingSolarKwdc"
              control={formInstance.control}
              label={
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Existing Solar System: kW-DC
                  <InfoIcon tooltipText="If no Existing Solar System, enter 0" />
                </Box>
              }
              placeholder="Enter kW-DC"
              disabled={disabled}
              autoFormat={true}
              controlProps={{
                rules: {
                  required: hasExistingSolar ? "Required" : false,
                  validate: createSolarFieldValidator("existing"),
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormNumberInput
              name="existingSolarKwac"
              control={formInstance.control}
              label={
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Existing Solar System: kW-AC
                  <InfoIcon tooltipText="If no Existing Solar System, enter 0" />
                </Box>
              }
              placeholder="Enter kW-AC"
              disabled={disabled}
              autoFormat={true}
              controlProps={{
                rules: {
                  required: hasExistingSolar ? "Required" : false,
                  validate: createSolarFieldValidator("existing"),
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormNumberInput
              name="existingSolarAnnualProduction"
              control={formInstance.control}
              label={
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Estimated Annual Solar Production (kWh)
                  <InfoIcon tooltipText="If no Existing Solar System, enter 0" />
                </Box>
              }
              placeholder="Enter annual production (kWh)"
              disabled={disabled}
              autoFormat={true}
              controlProps={{
                rules: {
                  required: hasExistingSolar ? "Required" : false,
                  validate: createSolarFieldValidator("existing"),
                },
              }}
            />
          </Grid>
        </Grid>
      )}

      {showNewFields && (
        <Box sx={{ mb: 2 }}>
          <FormRadioList
            name="existingSolarLessThan12Months"
            control={formInstance.control}
            label="Has the existing solar system been installed for less than 12 months?"
            items={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
            disabled={checkFieldsAreDisabled}
            controlProps={{
              rules: { required: false },
            }}
          />

          {existingSolarLessThan12Months === "yes" && (
            <Alert
              severity="info"
              sx={{
                fontSize: "13px",
                "& .MuiAlert-message": { fontSize: "13px", color: "#000" },
              }}
            >
              Note: If the solar system has been installed for less than 12
              months, the online System Sizing Tool may be harder to pass due to
              limited solar production. If you can't achieve a sufficient
              result, you may need to use the Manual System Sizing Tool for
              review and approval.
            </Alert>
          )}
        </Box>
      )}

      {hasNewSolar && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <FormNumberInput
              name="newSolarKwdc"
              control={formInstance.control}
              label={
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  New Solar System: kW-DC
                  <InfoIcon tooltipText="If no New Solar System, enter 0" />
                </Box>
              }
              placeholder="Enter kW-DC"
              disabled={disabled}
              autoFormat={true}
              controlProps={{
                rules: {
                  required: hasNewSolar ? "Required" : false,
                  validate: createSolarFieldValidator("new"),
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormNumberInput
              name="newSolarKwac"
              control={formInstance.control}
              label={
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  New Solar System: kW-AC
                  <InfoIcon tooltipText="If no New Solar System, enter 0" />
                </Box>
              }
              placeholder="Enter kW-AC"
              disabled={disabled}
              autoFormat={true}
              controlProps={{
                rules: {
                  required: hasNewSolar ? "Required" : false,
                  validate: createSolarFieldValidator("new"),
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormNumberInput
              name="newSolarAnnualProduction"
              control={formInstance.control}
              label={
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Estimated Annual Solar Production (kWh)
                  <InfoIcon tooltipText="If no New Solar System, enter 0" />
                </Box>
              }
              placeholder="Enter annual production (kWh)"
              disabled={disabled}
              autoFormat={true}
              controlProps={{
                rules: {
                  required: hasNewSolar ? "Required" : false,
                  validate: createSolarFieldValidator("new"),
                },
              }}
            />
          </Grid>
        </Grid>
      )}

      {showNewFields && (
        <Box sx={{ mb: 0, mt: 0 }}>
          <FormCheckbox
            value={!!hasExistingBattery}
            onChange={(value) => {
              formInstance.setValue("hasExistingBattery", value);
              setSizingForm({
                ...sizingForm,
                hasExistingBattery: value,
              });
            }}
            label="Existing Battery: Does the site already have a battery installed?"
            disabled={checkFieldsAreDisabled}
          />

          {hasExistingBattery && (
            <Alert
              severity="info"
              sx={{
                fontSize: "13px",
                "& .MuiAlert-message": { fontSize: "13px", color: "#000" },
              }}
            >
              The Online System Sizing Tool cannot be used for sites with an
              existing battery. If you're adding one or more new batteries, you
              must complete the Manual System Sizing Tool and submit it for
              review.
            </Alert>
          )}
        </Box>
      )}

      <Box sx={{ mb: 1, mt: 0.5 }}>
        <AsyncSelectWithSearch
          name="batteryManufacturer"
          idKey="batteryManufacturerId"
          control={formInstance.control}
          label="Battery Manufacturer"
          getItems={getBatteryOems}
          itemsKey="oems"
          placeholder="Select battery manufacturer"
          disabled={disabled}
          controlProps={{ rules: { required: !hasExistingBattery } }}
          onValueChange={() => {
            const formValues = formInstance.getValues();
            setSizingForm({
              ...sizingForm,
              batteryManufacturer: formValues.batteryManufacturer,
              batteryManufacturerId: formValues.batteryManufacturerId,
              batteryProductName: "",
              batteryQuantity: "",
            });
          }}
        />
      </Box>

      <Box sx={{ mb: 1 }}>
        <AsyncSelectWithSearch
          name="batteryProductName"
          idKey="batteryProductId"
          control={formInstance.control}
          label="Battery Product Name"
          getItems={getBatteryListWithId}
          itemsKey="batteryModels"
          placeholder="Select battery product"
          disabled={disabled}
          controlProps={{ rules: { required: !hasExistingBattery } }}
          onValueChange={() => {
            const formValues = formInstance.getValues();
            setSizingForm({
              ...sizingForm,
              batteryQuantity: "",
            });

            if (onBatteryProductChange && formValues.batteryProductName) {
              onBatteryProductChange(formValues.batteryProductName);
            }
          }}
        />
      </Box>

      <Box sx={{ mb: 1 }}>
        <FormNumberInput
          name="batteryQuantity"
          control={formInstance.control}
          label="Battery Quantity"
          controlProps={{
            rules: {
              required: hasExistingBattery
                ? false
                : (((value: FormValidationValue) => {
                    if (!value && value !== 0) {
                      return "Battery quantity is required";
                    }

                    const num = Number(value);

                    if (
                      Number.isNaN(num) ||
                      num <= 0 ||
                      !Number.isFinite(num) ||
                      !Number.isInteger(num)
                    ) {
                      return "Battery quantity is required";
                    }

                    return true;
                  }) as any),
              validate: {
                positiveNumber: (value: FormValidationValue) => {
                  if (hasExistingBattery) return true;
                  const num = Number(value);
                  if (Number.isNaN(num) || num <= 0) {
                    return "Battery quantity must be a positive number";
                  }
                  if (!Number.isInteger(num)) {
                    return "Battery quantity must be a whole number";
                  }
                  return true;
                },
              },
            },
          }}
          placeholder="2"
          disabled={disabled}
          autoFormat={false}
        />
      </Box>
    </>
  );
};

export default SystemSpecificationsSection;
